import { CommonModule } from '@angular/common';
import { Component, OnInit, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { PssLoadingComponent } from '../../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ParkingSubscriptionListRow } from '../../../../../core/models/finance/master-fee.model';
import { MapperMasterFeeService } from '../../../../../core/mappers/finance/mapper-master-fee.service';
import { ApiMasterFeeService } from '../../../../../core/services/api/finance/api-master-fee.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../../../core/services/site-state.service';
import { ModalParkingSubscriptionComponent } from '../../../modals/finance/modal-parking-subscription/modal-parking-subscription.component';

@Component({
  selector: 'app-manage-subscription',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalParkingSubscriptionComponent],
  templateUrl: './manage-subscription.component.html',
  styleUrl: './manage-subscription.component.css',
})
export class ManageSubscriptionComponent implements OnInit {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiMasterFeeService);
  private readonly mapper = inject(MapperMasterFeeService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  items = signal<ParkingSubscriptionListRow[]>([]);
  isLoading = signal(false);
  searchText = signal('');
  currentPage = signal(1);
  pageSize = signal(20);
  pageSizeOptions: number[] = [10, 20, 50, 100];
  totalItems = signal(0);
  totalPages = signal(1);

  isModalOpen = signal(false);
  selectedItem = signal<ParkingSubscriptionListRow | null>(null);

  constructor() {
    effect(() => {
      const site = this.siteState.selectedSite();
      if (!site?.id) {
        untracked(() => {
          this.items.set([]);
          this.totalItems.set(0);
          this.totalPages.set(1);
        });
        return;
      }
      untracked(() => {
        this.currentPage.set(1);
        this.loadTable();
      });
    });
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get selectedSiteName(): string {
    return this.siteState.selectedSite()?.name ?? '';
  }

  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  ngOnInit(): void {
    this.loadTable();
  }

  get pagesArray(): number[] {
    const total = this.totalPages();
    const maxVisible = 5;
    let start = Math.max(1, this.currentPage() - 2);
    let end = Math.min(total, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  get startItemIndex(): number {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage() * this.pageSize(), this.totalItems());
  }

  onRefresh(): void {
    this.currentPage.set(1);
    this.loadTable();
  }

  onSearch(): void {
    this.currentPage.set(1);
    this.loadTable();
  }

  onPageSizeChange(event: Event): void {
    const element = event.target as HTMLSelectElement;
    this.pageSize.set(Number(element.value));
    this.currentPage.set(1);
    this.loadTable();
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
      this.loadTable();
    }
  }

  openCreate(): void {
    if (!this.canCreate) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }
    if (!this.selectedSiteId) {
      this.toast.error('กรุณาเลือกลานจาก Sidebar');
      return;
    }
    this.selectedItem.set(null);
    this.isModalOpen.set(true);
  }

  openEdit(item: ParkingSubscriptionListRow): void {
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    this.selectedItem.set(item);
    this.isModalOpen.set(true);
  }

  closeModal(): void {
    this.isModalOpen.set(false);
    this.selectedItem.set(null);
  }

  onSaved(): void {
    this.closeModal();
    this.loadTable();
  }

  formatDate(value?: string | null): string {
    if (!value) return '-';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString('th-TH');
  }

  private loadTable(): void {
    const siteId = this.selectedSiteId;
    if (!siteId) {
      this.items.set([]);
      this.totalItems.set(0);
      this.totalPages.set(1);
      this.isLoading.set(false);
      return;
    }

    this.isLoading.set(true);
    this.api.getParkingSubscriptionForTable(siteId, this.currentPage(), this.pageSize(), this.searchText()).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapSubscriptionTable(res);
        this.items.set(mapped.items);
        this.totalItems.set(mapped.total);
        this.totalPages.set(mapped.totalPages || Math.max(1, Math.ceil(mapped.total / this.pageSize())));
        this.isLoading.set(false);
      },
      error: () => {
        this.items.set([]);
        this.isLoading.set(false);
        this.toast.error('โหลดข้อมูล Subscription ล้มเหลว');
      },
    });
  }
}
